import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import ImageUploader, { resolveImageUrl } from "@/components/ImageUploader";
import { toast } from "sonner";
import { Pencil, Trash2, Save, X, Plus } from "lucide-react";

const empty = { slug: "", name_el: "", name_en: "", image: "" };

const AdminCategories = () => {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null); // id of row being edited or "new"
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/categories").then((r) => setCats(r.data || []));
  useEffect(() => {
    load();
  }, []);

  const startEdit = (cat) => {
    setEditing(cat.id || cat.slug);
    setForm({
      slug: cat.slug || "",
      name_el: cat.name_el || "",
      name_en: cat.name_en || "",
      image: cat.image || "",
    });
  };

  const startNew = () => {
    setEditing("new");
    setForm(empty);
  };

  const cancel = () => {
    setEditing(null);
    setForm(empty);
  };

  const save = async () => {
    setLoading(true);
    try {
      if (editing === "new") {
        if (!form.slug || !form.name_el || !form.name_en) {
          toast.error("Slug, Ελληνικό & Αγγλικό όνομα είναι υποχρεωτικά");
          return;
        }
        await api.post("/categories", form);
        toast.success("Η κατηγορία δημιουργήθηκε");
      } else {
        await api.put(`/categories/${editing}`, form);
        toast.success("Η κατηγορία ενημερώθηκε");
      }
      await load();
      cancel();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (cat) => {
    if (!window.confirm(`Διαγραφή κατηγορίας "${cat.name_el}";`)) return;
    try {
      await api.delete(`/categories/${cat.id || cat.slug}`);
      toast.success("Διαγράφηκε");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    }
  };

  return (
    <div className="space-y-5" data-testid="categories-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-2xl">Κατηγορίες</h3>
        {editing === null && (
          <button
            className="luxury-btn"
            onClick={startNew}
            data-testid="category-new"
          >
            <Plus size={14} /> Νέα κατηγορία
          </button>
        )}
      </div>

      {/* Editing form */}
      {editing !== null && (
        <div className="bg-white border border-[#EBE5DF] p-6 space-y-4" data-testid="category-form">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="overline mb-1 block text-[#736B66]">Slug</label>
              <input
                className="luxury-input-box w-full"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="skylos"
                disabled={editing !== "new"}
                data-testid="category-slug"
              />
            </div>
            <div>
              <label className="overline mb-1 block text-[#736B66]">Όνομα (EL)</label>
              <input
                className="luxury-input-box w-full"
                value={form.name_el}
                onChange={(e) => setForm({ ...form, name_el: e.target.value })}
                data-testid="category-name-el"
              />
            </div>
            <div>
              <label className="overline mb-1 block text-[#736B66]">Name (EN)</label>
              <input
                className="luxury-input-box w-full"
                value={form.name_en}
                onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                data-testid="category-name-en"
              />
            </div>
          </div>
          <div>
            <label className="overline mb-1 block text-[#736B66]">Εικόνα</label>
            <ImageUploader
              value={form.image ? [form.image] : []}
              onChange={(arr) => setForm({ ...form, image: arr[0] || "" })}
              max={1}
            />
          </div>
          <div className="flex gap-3">
            <button
              className="luxury-btn"
              onClick={save}
              disabled={loading}
              data-testid="category-save"
            >
              <Save size={14} /> Αποθήκευση
            </button>
            <button className="luxury-btn-outline" onClick={cancel} data-testid="category-cancel">
              <X size={14} /> Άκυρο
            </button>
          </div>
        </div>
      )}

      {/* Listing */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cats.map((c) => (
          <div
            key={c.id || c.slug}
            className="bg-white border border-[#EBE5DF] overflow-hidden"
            data-testid={`category-row-${c.slug}`}
          >
            <div className="aspect-[5/4] bg-[#F5F2ED] overflow-hidden">
              {c.image ? (
                <img
                  src={resolveImageUrl(c.image)}
                  alt={c.name_el}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#736B66] text-sm">
                  No image
                </div>
              )}
            </div>
            <div className="p-4 flex items-center justify-between">
              <div>
                <p className="font-semibold">{c.name_el}</p>
                <p className="text-xs text-[#736B66]">/{c.slug} · {c.name_en}</p>
              </div>
              <div className="flex gap-2">
                <button
                  className="p-2 hover:bg-[#F5F2ED]"
                  onClick={() => startEdit(c)}
                  data-testid={`category-edit-${c.slug}`}
                >
                  <Pencil size={16} />
                </button>
                <button
                  className="p-2 hover:bg-[#F5F2ED] text-[#8B0000]"
                  onClick={() => remove(c)}
                  data-testid={`category-delete-${c.slug}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminCategories;
